import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapGoogle } from '../../components/MapGoogle';
import { FilteredLocation } from '../../components/FilteredLocation';
import { LocationContext } from '../../contexts/LocationContext';
import { AccessibilityContext } from '../../contexts/AccessibilityContext';
import { getCityForm, getObjectForm, getPunktForm } from '../../helpers/correctForm';
import { filterLocations, getCoordinates, groupLocations, LocationSort, parseLocationSearch, POINT_TYPES, PointType } from '../../helpers/locationBrowse';
import { readLocationPreferences, writeLocationPreferences } from '../../helpers/locationPreferences';
import { LocationTypeFilter } from './LocationTypeFilter';
import './Localization.css';

const MemoizedMapGoogle = React.memo(MapGoogle);

export const Localization = () => {
    const { locations, locationsStatus, reloadLocations, userPosition, setUserPosition } = useContext(LocationContext);
    const loading = locationsStatus === 'loading';
    const ready = locationsStatus === 'ready';
    const { isContrast } = useContext(AccessibilityContext);
    const { t } = useTranslation();
    const [preferences] = useState(readLocationPreferences);
    const [searchParams, setSearchParams] = useSearchParams();
    const paramsRef = useRef({ params: searchParams, setSearchParams });
    paramsRef.current = { params: searchParams, setSearchParams };
    const [search, setSearch] = useState('');
    const [isOpenTooltip, setIsOpenTooltip] = useState(false);
    const headerRef = useRef<HTMLHeadingElement>(null);
    const [locating, setLocating] = useState(false);
    const [locationMessage, setLocationMessage] = useState('');
    const submittedSearch = searchParams.get('search') || '';
    const searchError = useMemo(() => parseLocationSearch(submittedSearch).error, [submittedSearch]);
    const typesParam = searchParams.get('types') ?? preferences.types.join(',');
    const selectedTypes = useMemo(() => POINT_TYPES.filter(type => typesParam.split(',').includes(type)), [typesParam]);
    const sortParam = searchParams.get('sort') ?? preferences.sort;
    const requestedSort: LocationSort = sortParam === 'distance' || sortParam === 'installationDate' ? sortParam : 'alphabetical';
    const sort: LocationSort = requestedSort === 'installationDate' ? 'installationDate'
        : requestedSort === 'distance' && userPosition ? 'distance' : 'alphabetical';
    const filteredLocations = useMemo(() => filterLocations(locations, selectedTypes, submittedSearch),
        [locations, selectedTypes, submittedSearch]);
    const groups = useMemo(() => groupLocations(filteredLocations, sort, userPosition), [filteredLocations, sort, userPosition]);
    const totalUniqueAddresses = groups.reduce((total, city) => total + city.objects.length, 0);
    const totalCities = groups.filter(group => group.city !== '').length;

    useEffect(() => { headerRef.current?.focus(); }, []);
    useEffect(() => { setSearch(submittedSearch); }, [submittedSearch]);
    useEffect(() => {
        writeLocationPreferences({ types: selectedTypes, sort: requestedSort });
    }, [selectedTypes, requestedSort]);

    const updateParam = useCallback((key: string, value: string, resetExpanded = false, replace = false) => {
        const params = new URLSearchParams(paramsRef.current.params);
        if (!params.has('types')) params.set('types', preferences.types.join(','));
        if (!params.has('sort')) params.set('sort', preferences.sort);
        params.set(key, value);
        if (resetExpanded) {
            params.delete('expandedCity');
            params.delete('expandedLocation');
        }
        paramsRef.current.params = params;
        paramsRef.current.setSearchParams(params, { preventScrollReset: true, replace });
    }, [preferences]);
    const handleSearchSubmit = () => updateParam('search', search.trim(), true);
    const toggleType = (type: PointType) => {
        const next = POINT_TYPES.filter(item => item === type ? !selectedTypes.includes(item) : selectedTypes.includes(item));
        updateParam('types', next.join(','), true);
    };
    useEffect(() => {
        if (requestedSort !== 'distance' || userPosition) {
            setLocating(false);
            return;
        }
        let active = true;
        setLocating(true);
        setLocationMessage('');
        const fail = (message: string) => {
            if (!active) return;
            setLocating(false);
            setLocationMessage(message);
            updateParam('sort', 'alphabetical', false, true);
        };
        const timer = window.setTimeout(() => {
            if (!navigator.geolocation) {
                fail('Ta przeglądarka nie obsługuje lokalizacji.');
                return;
            }
            try {
                navigator.geolocation.getCurrentPosition(result => {
                    if (!active) return;
                    const coords = getCoordinates({ loclat: String(result.coords.latitude), loclon: String(result.coords.longitude) });
                    if (!coords) {
                        fail('Nie udało się ustalić lokalizacji. Spróbuj ponownie.');
                        return;
                    }
                    setLocating(false);
                    setUserPosition(coords);
                }, error => {
                    fail(error.code === 1 ? 'Zezwól na lokalizację w ustawieniach przeglądarki.'
                        : 'Nie udało się ustalić lokalizacji. Spróbuj ponownie.');
                }, { timeout: 10000, maximumAge: 60000 });
            } catch {
                fail('Nie udało się ustalić lokalizacji. Spróbuj ponownie.');
            }
        }, 0);
        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [requestedSort, userPosition, setUserPosition, updateParam]);

    const handleSortChange = (value: string) => {
        setLocationMessage('');
        updateParam('sort', value);
    };

    return (
        <section className={`location-browser${isContrast ? ' location-browser--contrast' : ''}`}>
            <h1 ref={headerRef} tabIndex={-1}
                className="location-page-title">
                {t('Lokalizacje')} TOTUPOINT
            </h1>
            <div className="location-toolbar">
                <form className="location-search" role="search" onSubmit={event => { event.preventDefault(); handleSearchSubmit(); }}>
                    <label htmlFor="default-search" className="sr-only">{t('Wyszukaj')}</label>
                    <div className="location-search-field">
                        <svg aria-hidden="true" className="location-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input type="search" id="default-search"
                            aria-invalid={searchError && search === submittedSearch ? true : undefined}
                            aria-describedby={searchError ? 'location-search-error' : undefined}
                            placeholder={t('Miejscowość, obiekt lub adres')} value={search} onChange={event => setSearch(event.target.value)} />
                    </div>
                    <button type="submit" aria-label={t('Wyszukaj lokalizacje')} className="location-search-submit">
                        {t('Szukaj')}
                    </button>
                    <button type="button" onClick={() => setIsOpenTooltip(!isOpenTooltip)}
                        onKeyDown={event => { if (event.key === 'Escape') setIsOpenTooltip(false); }}
                        title={t('Rozwiń więcej informacji o wyszukiwaniu')}
                        aria-label={t('Rozwiń więcej informacji o wyszukiwaniu')} aria-expanded={isOpenTooltip} aria-controls={isOpenTooltip ? 'search-help' : undefined}
                        className="location-help-button">
                        <svg aria-hidden="true" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-6 h-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75 M21 12a9 9 0 11-18 0 9 9 0 0118 0z m-9 5.25h.008v.008H12v-.008z" />
                        </svg>
                    </button>
                </form>
                {searchError && <p id="location-search-error" role="alert" className="location-search-error">
                    {t(`locationSearch.${searchError}`)}
                </p>}
                {isOpenTooltip && <div id="search-help" className="location-help-content">
                    <p>{t('locationSearch.helpText')}</p>
                    <p>{t('locationSearch.helpDates')}</p>
                    <ul>
                        <li><code>@2026</code> {t('locationSearch.exampleYear')}</li>
                        <li><code>@2026-08</code> {t('locationSearch.exampleMonth')}</li>
                        <li><code>@2026-08-15</code> {t('locationSearch.exampleDay')}</li>
                        <li><code>@2025...2026-03</code> {t('locationSearch.exampleRange')}</li>
                    </ul>
                    <p>{t('locationSearch.helpRange')}</p>
                    <p>{t('locationSearch.helpCombined')}</p>
                </div>}
                <LocationTypeFilter selectedTypes={selectedTypes} onToggle={toggleType} />
            </div>
            <div className="location-results">
                <div className="location-results-heading">
                    <h2>{t('Lista lokalizacji')}</h2>
                    <span role="status" className="location-results-count">
                        {ready && !searchError && <>{t('Wyszukano')}: {filteredLocations.length} {t(getPunktForm(filteredLocations.length))}, {totalUniqueAddresses} {t(getObjectForm(totalUniqueAddresses))}, {totalCities} {t(getCityForm(totalCities))}</>}
                    </span>
                </div>
                <div className="location-results-toolbar">
                    <div className="location-sort-control">
                        <label htmlFor="location-sort" className="location-control-label">{t('Sortuj')}</label>
                        <select id="location-sort" value={requestedSort} onChange={event => handleSortChange(event.target.value)}
                            aria-describedby={locating || locationMessage || (!userPosition && requestedSort === 'distance') ? 'location-permission-status' : undefined}
                            className="location-sort-select">
                            <option value="alphabetical">{t('Alfabetycznie')}</option>
                            <option value="distance">{t('Po odległości')}{!userPosition ? ` (${t('udostępnij lokalizację')})` : ''}</option>
                            <option value="installationDate">{t('Po dacie instalacji')}</option>
                        </select>
                    </div>
                    <div id="location-permission-status" role="status" className="location-permission-status">
                        {locating ? t('Ustalanie lokalizacji...') : locationMessage ? t(locationMessage) : null}
                        {!locating && !userPosition && (locationMessage || requestedSort === 'distance') && (
                            <button type="button" onClick={() => handleSortChange('distance')} className="location-permission-action">{t('Udostępnij lokalizację')}</button>
                        )}
                    </div>
                </div>
                {!ready && (
                    <div className="location-load-state">
                        {loading ? (
                            <div role="status" className="location-loading-status">
                                <span className="location-loading-spinner" aria-hidden="true" />
                                <span>{t('Ładowanie punktów...')}</span>
                            </div>
                        ) : (
                            <>
                                <p role="alert">{t('Nie udało się pobrać punktów.')}</p>
                                <button type="button" className="location-search-submit" onClick={reloadLocations}>
                                    {t('Spróbuj ponownie')}
                                </button>
                            </>
                        )}
                    </div>
                )}
                <div className="location-results-list" role="region" aria-label={t('Lista lokalizacji')} aria-busy={loading} tabIndex={0}>
                    {ready && !searchError && (filteredLocations.length ? <FilteredLocation groups={groups} sort={sort} position={userPosition}
                        filterKey={JSON.stringify([submittedSearch, selectedTypes])} /> : (
                        <p className="location-empty">{t('Brak wyników')}</p>
                    ))}
                </div>
            </div>
            <div className="location-map" role="region" aria-label={t('Interaktywna mapa google - lokalizacja naszych znaczników')}>
                <MemoizedMapGoogle height="100%" width="100%" borderRadius="8px" className="h-full w-full" locationsFiltered={filteredLocations} />
            </div>
        </section>
    );
};
